import { LanguageKey, OptionProps, UnknownKeys } from 'typings/custom'
import type { AnalyticsEvent } from 'helpers/analytics'
import { ReactNode } from 'react'
import { I18N } from './custom'
import { StatementEnrollment } from './statement'
import { UserAccountInfo } from './userInfo'
import { CardType, PrivilegeModule } from './account'
import { Address } from './address'

export type SupportedLanguages = 'en' | 'es'

// ATTENTION! Use of UnknownKeys is only appropriate when brand is forced to use customized keys to its use case.
export type BrandTranslations = {
  [key in LanguageKey]: {
    common?: UnknownKeys
    customNeedAssistance?: UnknownKeys
    /** Used on CreditServicesList which appears in multiple components */
    creditServicesList?: {
      'Request Copies of BRAND receipt with Buyer Signature': string
    }
    csrNumber?: UnknownKeys
    csrAlertBanner?: UnknownKeys
    editUserInfo?: {
      'Credit service': string
    }
    registration: {
      pageTitle: string
    }
    /** All the below keys are related to FAQ */
    frequentlyAskedQuestions?: UnknownKeys
    faqLoginQuestions?: UnknownKeys
    faqLoginAnswers?: UnknownKeys
    faqPaymentQuestions?: UnknownKeys
    faqPaymentAnswers?: UnknownKeys
    faqInvoicesQuestions?: UnknownKeys
    faqInvoicesAnswers?: UnknownKeys
    faqTechnicalHelpQuestions?: UnknownKeys
    faqTechnicalHelpAnswers?: UnknownKeys
    faqInterestQuestions?: UnknownKeys
    faqInterestAnswers?: UnknownKeys
    faqWebPageIssueQuestions?: UnknownKeys
    faqWebPageIssueAnswers?: UnknownKeys
    faqCloseAccountQuestions?: UnknownKeys
    faqCloseAccountAnswers?: UnknownKeys
    faqContactUsQuestions?: UnknownKeys
    faqContactUsAnswers?: UnknownKeys
    faqUpdateAccountInformationQuestions?: UnknownKeys
    faqUpdateAccountInformationAnswers?: UnknownKeys
    faqAccountActivityQuestions?: UnknownKeys
    faqAccountActivityAnswers?: UnknownKeys
    faqReplacementCardsQuestions?: UnknownKeys
    faqReplacementCardsAnswers?: UnknownKeys
    faqSecurityInformationQuestions?: UnknownKeys
    faqSecurityInformationAnswers?: UnknownKeys
    faqCreditQuestions?: UnknownKeys
    faqCreditAnswers?: UnknownKeys
    faqTaxExemptionQuestions?: UnknownKeys
    faqTaxExemptionAnswers?: UnknownKeys
    faqOnlineMessageCenterQuestions?: UnknownKeys
    faqOnlineMessageCenterAnswers?: UnknownKeys
  }
}

export type LoginHeaderType = 'twocards' | 'singleimage' | 'background'

export type ModuleType = 'proxModule' | 'brcModule' | 'dualcardModule'

export type ClientAccountTypes =
  | 'lowesprox'
  | 'lowesbrc'
  | 'samsplcc'
  | 'samsdualcard'
  | 'samsprox'
  | 'flooranddecorbrc'
  | 'p66brc'
  | 'pocncibrc'

export type LegendTheme = {
  fill: string
  border: string
  text: string
}

/** Customer Support contact info */
export interface CustomerSupportContacts {
  contactUsProx: string
  contactUsBrc: string
  contactUsBrcPlcc: string
  contactUsBrcDc: string
  contactUsLba?: string
  closeAccountService: string
  getCSRNumber: (isProx?: boolean, cardType?: CardType) => string
  /** This phone number has many use cases and it should be implemented for all account types/clients. */
  creditServiceNumber: string
  contactUsDirectCustomer?: string
  membershipServiceNumber: string
  /** Provides list of localized contacts shown on the system error page. */
  systemErrorPageContacts: {
    en: string[]
    es: string[]
  }
  /** Array of contact numbers to be displayed on the Transmit 0003 - Invalid Input */
  assistanceAlertContacts: string[]
}

export interface AccountNumberConfig {
  /** minimum and maximum account number length */
  length: [min: number, max: number]
  /** Returns formatted account number using customized rules. optional */
  getCustomFormat?: (acctNumber: string) => string
  /**
   * Custom validator. Returns true when number is valid. Returns false when
   * invalid. Optional. Omit to use default validations.
   */
  customValidateNumber?: (acctNumber: string) => boolean
}

export interface CardNumberConfig {
  /** minimum and maximum length of card number */
  length: [min: number, max: number]
}

export interface ClientInfo {
  /** Unique identifier for the client brand */
  clientName: ClientKeys
  displayName: string
  /** Used for cases where brand desires to use different brand name than displayName */
  alternateDisplayName: string
  languages: SupportedLanguages[]
  /** This field doesn't appear to be read anywhere in the application. */
  vanityUrl: string
  /** This property doesn't appear to be read anywhere. Is it safe to remove? */
  hostname: string
  logoAltText: string
  /** Used as alt text for brand logo in invoice details section */
  invoiceDetailLogoAltText: string
  /** This property defines display online Administrator content at Online Access Error Banner in Login Page */
  isShowOnlineAdministrator: boolean
  /**
   * This property defines the style the LoginHeader will have.
   *
   * TODO: It looks like this value is no longer used in the app. Can it be
   * removed? Possibly replaced by implementation of Custom and Default
   * LoginHeaderCards components.
   */
  loginHeaderType: LoginHeaderType
  loginAccountTooltipTextAllowProx?: {
    en: string
    es: string
  }
  video: {
    /** Path to fetch video list json file for PROX accounts from S3 storage */
    proxVideoListPath?: string
    /** Path to fetch video list json file for BRC accounts from S3 storage */
    brcVideoListPath?: string
    /** Hosting service base url path for videos. */
    videoHost: 'https://player.vimeo.com/video/'
  }
  apigeeClientId: {
    development: string
    production: string
  }
  customerSupport: CustomerSupportContacts
  /**
   * External URL to brand hosted member benefits page for BRC (PLCC) rewards
   * members. Currently, this is only implemented for Lowes/BRC FAQ > Tax Exempt
   * section.
   */
  brcRewardsBenefitsURL?: string
  address?: {
    /**
     * Configure prox payment address used for invoice/credit details
     */
    proxPayment?: {
      city: string
      state: string
      streetNumber: string
      postalCode: string
    }
    payment: {
      city: string
      state: string
      streetNumber: string
      postalCode: string
    }
    paymentResearch: {
      city: string
      state: string
      streetNumber: string
      postalCode: string
    }
    proxInvoiceDispute?: Address & {
      faxNumber: string
    }
    /**
     * This field does not appear to be read anywhere. Can it be safely
     * removed?
     */
    consent: {
      city: string
      state: string
      /** This is the zip code */
      streetNumber: string
      /** This is the PO BOX */
      postalCode: string
    }
    /** This address is consumed by a single FAQ question */
    businessPayments: {
      city: string
      state: string
      streetNumber: string
      postalCode: string
    }
    credit?: {
      city: string
      state: string
      streetNumber: string
      postalCode: string
    }
  }
  legacySiteUrl?: string
  /**
   * Can this be removed? It doesn't appear to be read anywhere in the
   * application.
   */
  websiteUrl?: string
  /**
   * The value of this is the same across all brands. Should it be removed from
   * this interface and configured via app config instead?
   */
  consentToEcomUrl?: string
  /**
   * Only used for sams client in a custom component. Suggestion: remove from
   * this interface and hard code directly in the Sam's custom component.
   */
  lookUpUserIdUrl?: string
  faqOptions?: {
    /**
     * Set to true to include the Tax Exemption section in the User Guide > FAQ
     */
    includeTaxExemptSection: boolean
  }
  privileges: {
    prox?: PrivilegeModule
    brc?: PrivilegeModule
    dc?: PrivilegeModule
  }
  /**
   * Brand should use a form for users to request additional cards when set to
   * true. False/undefined will result in contact phone number presented to the
   * user instead of a form.
   */
  useRequestAdditionalBuyerCardsForm?: boolean
  /**
   * Set to true to display the Statement List in the Statement page for prox accounts
   */
  showStatementList?: boolean
  /**
   * Return true to display Welcome Mat on the account summary page. Return false to hide it.
   */
  showWelcomeMat: (accountType: UserAccountInfo['accountType']) => boolean
  /**
   * Returns the business tax classification list dropdown values in Request Credit Line Increase Automation page.
   * Applicable for Lowex prox, Lowes plcc and Sams plcc
   */
  getCustomTaxClassificationList?: (i18n: I18N) => Array<OptionProps>
  /**
   * Returns the welcome message displayed on the login page
   */
  getLoginWelcomeMessage: (i18n: I18N) => string
  /** Returns transaction client name based on the account type */
  getTransactionClientName: (isProx: boolean) => string
  /**
   * Returns the edit buyer error message to contact customer care
   * Applicable only for prox accounts
   */
  getBuyerCustomerCareMessage?: (i18n: I18N) => string
  /**
   * Return true to display card reissue modal on the account summary page. Return false to hide it.
   */
  showCardReissueModal: (accountType: UserAccountInfo['accountType']) => boolean
}

interface ClientDetailAddress {
  streetNumber: string
  postalCode: string
}

export interface ClientDetail {
  /** Display name for the store/brand. Appears in a number of places including IE message, Login page, and welcome mat */
  storeName: string
  /** Display name of payee for prox payments */
  payableAccount: string
  /** Display name of payee for brc payments */
  payableAccountBRC: string
  /** Used as part of the addressee in various mailing addresses in the credit FAQ section. */
  payableAccountAcronym: string
  /** Page title for withdrawal of EComm consent used in electronic comm. terms and conditions. */
  creditAccount: string
  /** Customer service message recipient title for replies in message center */
  messageTo: string
  /** Bit array used as default user privileges when user registers without an auth code  */
  securityLevel: string
  /** Addresses for various FAQ answers */
  address: {
    /** Address for updating Legal Name Change in Update contact information (FAQ) */
    brcLegalNameChange: Address
    /** Address for disputing incorrect payment amount for BRC accounts. */
    brcPaymentResearch: Address
    /** Address for Credit Bureau Questions/Disputes in user guide */
    brcCreditBureau: Address
    /** Address for Credit Bureau Question/Disputes for prox account */
    proxCreditBureau?: Address
    /** Address for Payment Research for prox accounts */
    proxPaymentResearch?: Address
    /** Payment address appearing on BRC Payment Details */
    payment?: ClientDetailAddress
  }
  /** Client specific subject options for secure message modal */
  secureMessageSubjectOptions?: string[]
  /** Configure the welcome mat. Provide empty strings if welcome mat is not applicable. */
  welcomeMatConfig: {
    headingBrandName: string
    csDisplayName: string
    csNumber: string
  }
}

export interface ClientPalette {
  primary: string
  secondary: string
  primaryDark: string
  backToTop: {
    shadow: string
    background: string
    icon: string
    hover: {
      background: string
      icon: string
    }
    active: {
      background: string
      icon: string
    }
  }
  cobrowse: {
    text: string
    hover: string
  }
  titleBar: string
  cardHeader: {
    backgroundStart: string
    backgroundEnd: string
  }
  menu: {
    // Used for define Menu Link Text color
    text: string
    // Used for define Active Menu border color
    border: string
  }
  footerLink: string
  hoverBackground: string
  assistanceBackground: string
  /*
   * Modal background is solid. We should give same color code backgroundStart and backgroundend
   * Modal background is gradient. backgroundStart color is gradient start color code and backgroundend color is gradient end color code
   */
  modal: {
    backgroundStart: string
    backgroundEnd: string
    text: string
  }
  header: {
    background: string
    text: string
    border: string
  }
  headerMenu: {
    color: string
    backgroundColorStart: string
    backgroundColorEnd: string
  }
  welcomeMat: {
    backgroundColorStart: string
    backgroundColorEnd: string
    /** Used for the color of links available in Welcome Mat */
    link: string
  }
  offerSection: {
    background: string
    text: string
    border: string
  }
  button: {
    primary: {
      text: string
      hover: string
      active: string
    }
    secondary: {
      text: string
      hover: string
      active: string
    }
    text: string
    background: string
  }
  navigation: {
    /** Used for the text color of active tab */
    activeTabText: string
    activeTabBorder: string
    dropdownHover: string
    /** Used for the text color of inactive tab */
    inactiveTabText: string
  }
  radio: {
    border: string
    background: string
    inactiveBorder: string
    focusBackground: string
  }
  /**
   * radioGroupCard used for define border and background color of checked customized Radio control.
   */
  radioGroupCard: {
    checkedBorder: string
    checkedBackground: string
  }
  checkbox: {
    border: string
    background: string
    font: string
  }
  field: {
    icon: string
    focus: string
    label: string
    background: string
  }
  datepicker: {
    navIcon: string
    shadow: string
    disabled: string
    legend: {
      selectedDate: LegendTheme
      dueDate: LegendTheme
    }
  }
  tooltip: {
    borderColor: string
    backgroundColor: string
    color: string
  }
  loader: string
  body: string
  // Used for define Router Link Text color
  link: string
  /** CL Switch component color */
  switch: {
    background: string
  }
}

export interface ClientImages {
  logo: string
  brandLogo: string
  proxCard: string
  brcCard: string
  dcCard: string
  /** Login header image. */
  loginHeaderCardsImage: string
  /** Alternative image used if client supports prox accounts along with BRC accounts.  */
  loginHeaderCardsWithProxImage?: string
  /**
   * Path to marketing banner image in the S3 assets bucket. This is consumed by
   * getMarkingBannerConfig.
   */
  banner: {
    desktop: string
    mobile: string
  }
  /**
   * Path to marketing banner image in the S3 assets bucket. This is consumed by
   * getMarkingBannerConfig when the brand requires a distinct image from the
   * images linked by the banner.desktop and banner.mobile fields.
   */
  plccBanner?: {
    desktop: string
    mobile: string
  }
  monthlyStatement: string
  welcomeLetter: string
  offerBanner?: string
  /** Desktop Banner Image URL for card activation feature */
  cardActivationBannerDesktop?: string
  /** Mobile Banner Image URL for card activation feature */
  cardActivationBannerMobile?: string
  /** Desktop Card Reissue Issue Banner Image URL */
  cardReissueDesktop?: string
  /** Mobile Card Reissue Issue Banner 1 Image URL */
  cardReissueMobile1?: string
  /** Desktop Card Reissue Issue Banner 2 Image URL */
  cardReissueMobile2?: string
}

export interface ClientDocuments {
  closeAccountForm: string
  websiteUsage: string
  privacyPolicy: string
}

/**
 * Properties required to render the marketing banner and optional link to
 * promotional page.
 */
export interface MarketingBannerConfig {
  /**
   * URL for the marking banner image.
   */
  imgUrl: string
  /**
   * Set to true to include large padding above and below the banner.
   */
  includeLargeVerticalPadding?: boolean
}

/**
 * Returns configuration properties for the marketing banner. Returns null if
 * no marketing banner should be displayed for the given params.
 */
export interface GetMarketingBannerConfig {
  (params: {
    /** i18n translation method used to lookup alt text */
    i18n: I18N
    /** assetsUrl from config used to create the image url */
    assetsUrl: string
    /**
     * Some brands have marketing banner rules that depend on the type of card
     */
    cardType: CardType
    /**
     * true indicates that the account type is prox. (Apparently cardType is not
     * reliably set for prox accounts.)
     */
    isProx: boolean
    /**
     * True indicates that the image url returned by the function should point
     * to the mobile image.
     */
    isMobile: boolean
    /**
     * Some brands have marketing banner rules that depend on e-statement
     * enrollment status.
     */
    statementEnrollment: StatementEnrollment
  }): MarketingBannerConfig | null
}

export enum CSRAlertBannerType {
  /** For define OnlineAccess alert banner in Login */
  ONLINEACCESS = 'ONLINEACCESS',
  /** For define User Account locked alert banner in Login */
  ACCOUNTLOCK = 'ACCOUNTLOCK',
  /** For define Registraion info alert banner when user select without Authcode */
  REGISTRATIONINFO = 'REGISTRATIONINFO',
  /** For define Registraion Account Locked alertbanner in Registration */
  REGISTRATIONACCOUNTLOCK = 'REGISTRATIONACCOUNTLOCK',
  /** For define alertbanner When user provide invalid zipcode in Registration */
  INVALIDACCOUNT = 'INVALIDACCOUNT'
}

interface FAQAccess {
  hasInvoiceAccess: boolean
  hasPaymentAccess: boolean
  hasCreditAccess: boolean
}

export interface CustomBrandComponents {
  /**
   * Presents custom account number information for registration and similar
   * use cases.
   */
  CustomAccountNumberInfo?: React.FC<{ i18n: I18N; isRegistration: boolean }>
  CustomNeedAssistanceContent: React.FC<{ i18n: I18N }>
  CustomOfferInfo?: React.FC<{
    offerBanner: string
    offerPromoUrl: string
    offerText: string
    dataObject: string
    dataReason: string
    dataType: string
    dataAnalytics: AnalyticsEvent
  }>
  CustomLoginAcctLookupTooltip?: React.FC<{ i18n: I18N }>
  CsrAlertContent: React.FC<{ i18n: I18N; type?: CSRAlertBannerType }>
  CustomLoginHeaderCards?: React.FC<{
    assetHost: string
  }>
  /** Contact Us page brand phone number */
  ContactUsPhoneNumber: React.FC<{
    icon: ReactNode
    isProx: boolean
    cardType: CardType
  }>
  /** Contact Us page business hours */
  ContactUsBusinessHours: React.FC<{
    i18n: I18N
    isProx: boolean
    cardType: CardType
  }>
  FaqQuestions?: React.FC<{ i18n: I18N; access: FAQAccess }>
}

/**
 * Used to display the Credit Limit Increase view
 * i.e. Legacy CLI , CLI Automation and CSR Contact Information
 */
export enum CLIViews {
  CONTACT_INFO = 'CONTACT_INFO',
  LEGACY_CLI = 'LEGACY_CLI',
  CLI_AUTOMATION = 'CLI_AUTOMATION'
}

/**
 * Configure features available for the brand.
 */
export interface BrandFeatureConfig {
  /**
   * Set to true to activate prox account features. IMPORTANT! Commercial api services must support
   * prox accounts for the brand. Do not set to true unless you are certain that the API services
   * support all necessary Prox account functions.
   */
  allowProx: boolean
  /** Enable extended buyer management features (add, remove, report lost/stolen, and request replacement cards) */
  extendedBuyerManagement?: boolean
  /**
   * Get the Credit Limit Increase view.
   * Currently , cardType is required for Sams
   */
  getCLIView: (cardType: CardType) => CLIViews

  /**
   * Set to true to enable the card reactivation (/card-activation) feature for a specific client.
   */
  cardActivation: boolean
}

export interface WorkStationConfig {
  expiryPageCSRNumber: {
    proxNo: string
    brcNo: string
  }
}

/**
 * Brand specific configuration for the card activation feature
 */
export interface CardActivationBrandConfig {
  /** Heading for the card activation landing page */
  landingPageHeading: string

  /**
   * Branding of the card that appears in various references to the card in
   * text.
   *
   * example: "MyLowe's Pro Rewards" is used in "You can start using your
   * MyLowe's Pro Rewards card now"
   */
  cardBrandName: string

  /**
   * This is the brand name for the old card which is being replaced.
   *
   * example: "Lowe’s Business Advantage" is used in "3 digit security code
   * on back of Lowe’s Business Advantage card."
   */
  oldCardBrandName: string

  /** Alt text for marketing banner on desktop displays */
  marketingBannerAltDesktop: string

  /** Alt text for marketing banner on mobile displays */
  marketingBannerAltMobile: string
}

export interface ClientBrand {
  accountNumberConfig: AccountNumberConfig
  cardNumberConfig: CardNumberConfig
  clientInfo: ClientInfo
  clientDetail: ClientDetail
  palette: ClientPalette
  images: ClientImages
  brandDocument: ClientDocuments
  getMarketingBannerConfig: GetMarketingBannerConfig
  customComponents: CustomBrandComponents
  translations: BrandTranslations
  features: BrandFeatureConfig
  workStationConfig: WorkStationConfig
  cardActivation?: CardActivationBrandConfig
}
